import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  HiBuildingOffice2,
  HiMagnifyingGlass,
  HiOutlineInformationCircle,
  HiQuestionMarkCircle,
  HiUser,
} from "react-icons/hi2";
import { Pie, PieChart } from "recharts";

import { useCompanyOwnership } from "@/hooks/api/company/useCompanyOwnership";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/atoms/chart";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import FeaturedText from "@/components/molecules/FeaturedText";

const chartConfig = {} satisfies ChartConfig;

export const colorsFixedPosition = [
  "var(--fg-brand-primary-subtle-hover)",
  "var(--fg-brand-primary-pressed)",
  "var(--fg-brand-secondary-subtle)",
  "var(--fg-success-primary)",
  "var(--fg-warning-primary)",
  "var(--fg-error-primary)",
  "var(--error-400)",
];

type Props = {
  id: string;
};

const CompanyDetailOwnerships = ({ id }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = useCompanyOwnership({ id, enabled: !!id });

  const total = useMemo(() => data?.parts.reduce((acc, item) => acc + (item.value || 0), 0) || 0, [data]);

  const formattedValue = useCallback((value: number | null) => {
    if (value === null) return "--";
    return `${value.toFixed(2)}%`;
  }, []);

  const displayDisclaimer = useMemo(() => total !== 100, [total]);

  if (isLoading || !data)
    return (
      <>
        <div className={"py-7"}>
          <div className={"mb-1.5 h-6 w-[30%] animate-pulse rounded-full bg-gray-300"} />

          <div className={"h-5 w-3/5 animate-pulse rounded-full bg-gray-300"} />
        </div>
        <div className={"flex"}>
          <div className={"mx-8 h-[200px] w-[200px] animate-pulse rounded-full border-[50px] border-gray-300"} />

          <div className={"flex items-center justify-center"}>
            <div>
              {Array.from({ length: 5 }).map((_, index) => (
                <div className={"flex gap-2 pb-1"} key={index}>
                  <div className={"h-5 w-5 animate-pulse rounded-md bg-gray-300"} />
                  <div className={"h-5 w-20 animate-pulse rounded-full bg-gray-300"} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </>
    );

  if (data.parts.length === 0)
    return (
      <div className={"py-10"}>
        <FeaturedText
          heading={t("search.noResults.title")}
          body={t("company.detail.overview.insights.ownership.noData")}
          size={"lg"}
          featuredIcon={{
            icon: HiMagnifyingGlass,
            color: "primary",
            size: "xl",
          }}
        />
      </div>
    );

  return (
    <>
      <div className={"py-7"}>
        <h3 className={"mb-1.5 font-semibold text-primary"}>{t("company.detail.overview.insights.ownership.title")}</h3>
        <p className={"text-sm text-tertiary"}>{t("company.detail.overview.insights.ownership.description")}</p>
      </div>
      <div className={"flex"}>
        <ChartContainer config={chartConfig} className="mx-8 aspect-square h-[200px]">
          <PieChart className={"h-full w-full"}>
            <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel suffix={"%"} />} />
            <Pie
              data={[
                ...data.parts.map((part, index) => ({
                  name: part.type === "OTHER" ? t("company.detail.overview.insights.ownership.other") : part.name,
                  amount: part.value,
                  fill: colorsFixedPosition[index],
                })),
                (total || 0) < 100 && {
                  name: t("company.detail.overview.insights.ownership.unknown"),
                  amount: 100 - (total || 0),
                  fill: "var(--fg-disabled-subtle)",
                },
              ]}
              dataKey="amount"
              nameKey="name"
              innerRadius={45}
              outerRadius={"100%"}
            />
          </PieChart>
        </ChartContainer>
        <div className={"flex items-center justify-center"}>
          <div>
            {data!.parts.map((item, index) => (
              <div key={item.name} className="flex items-center gap-2 pb-1">
                <div className={"w-5"}>
                  {item.type === "COMPANY" && (
                    <HiBuildingOffice2 className="size-5" style={{ fill: colorsFixedPosition[index] }} />
                  )}
                  {item.type === "PERSON" && <HiUser className="size-5" style={{ fill: colorsFixedPosition[index] }} />}
                  {item.type === "OTHER" && (
                    <div className="ml-1 size-3 rounded-full" style={{ background: colorsFixedPosition[index] }} />
                  )}
                </div>
                <span className={"text-sm text-tertiary"}>
                  {item.type === "OTHER"
                    ? `< ${formattedValue(item.value)}  ${t("company.detail.overview.insights.ownership.other")}`
                    : `${formattedValue(item.value)} ${item.name || t("company.detail.overview.insights.ownership.unknown")}`}
                </span>
              </div>
            ))}
            {total < 100 && (
              <div className="flex items-center gap-2">
                <HiQuestionMarkCircle className="size-5 text-gray-400" />
                <span className={"text-sm text-tertiary"}>
                  {formattedValue(100 - total)} {t("company.detail.overview.insights.ownership.unknown")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {displayDisclaimer && (
        <div className={"flex gap-2 py-4 pr-12"}>
          <FeaturedIcon icon={HiOutlineInformationCircle} size={"sm"} />
          <span className={"text-xs text-quaternary"}>
            {total > 100
              ? t("company.detail.overview.insights.ownership.disclaimer")
              : t("company.detail.overview.insights.ownership.disclaimerMissing")}
          </span>
        </div>
      )}
    </>
  );
};

export default CompanyDetailOwnerships;
